import { useCallback, useEffect, useRef, useState } from 'react';

interface UseSessionTimerOptions {
  onComplete?: () => void;
}

interface SessionTimerState {
  timeRemaining: number;
  totalDuration: number;
  isRunning: boolean;
  isPaused: boolean;
  progress: number;
}

interface SessionTimerActions {
  start: (durationSeconds: number) => void;
  pause: () => void;
  resume: () => void;
  togglePause: () => void;
  stop: () => void;
}

export type SessionTimer = SessionTimerState & SessionTimerActions;

export function useSessionTimer(options: UseSessionTimerOptions = {}): SessionTimer {
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [totalDuration, setTotalDuration] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const startTimeRef = useRef(0);
  const elapsedBeforePauseRef = useRef(0);
  const durationRef = useRef(0);
  const rafRef = useRef<number>(0);
  const onCompleteRef = useRef(options.onComplete);

  // Update ref during render (not in useEffect) to ensure it's current
  // before any rAF callbacks fire. This avoids stale callback issues.
  onCompleteRef.current = options.onComplete;

  const tick = useCallback(() => {
    const now = performance.now();
    const elapsed = elapsedBeforePauseRef.current + (now - startTimeRef.current) / 1000;
    const remaining = Math.max(0, durationRef.current - elapsed);

    setTimeRemaining(remaining);

    if (remaining <= 0) {
      setIsRunning(false);
      setIsPaused(false);
      onCompleteRef.current?.();
      return;
    }

    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const start = useCallback(
    (durationSeconds: number) => {
      cancelAnimationFrame(rafRef.current);
      durationRef.current = durationSeconds;
      elapsedBeforePauseRef.current = 0;
      startTimeRef.current = performance.now();
      setTotalDuration(durationSeconds);
      setTimeRemaining(durationSeconds);
      setIsRunning(true);
      setIsPaused(false);
      rafRef.current = requestAnimationFrame(tick);
    },
    [tick],
  );

  const pause = useCallback(() => {
    if (!isRunning || isPaused) return;
    cancelAnimationFrame(rafRef.current);
    const now = performance.now();
    elapsedBeforePauseRef.current += (now - startTimeRef.current) / 1000;
    setIsPaused(true);
  }, [isRunning, isPaused]);

  const resume = useCallback(() => {
    if (!isRunning || !isPaused) return;
    startTimeRef.current = performance.now();
    setIsPaused(false);
    rafRef.current = requestAnimationFrame(tick);
  }, [isRunning, isPaused, tick]);

  const togglePause = useCallback(() => {
    if (isPaused) {
      resume();
    } else {
      pause();
    }
  }, [isPaused, pause, resume]);

  const stop = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    setIsRunning(false);
    setIsPaused(false);
    setTimeRemaining(0);
  }, []);

  useEffect(() => {
    return () => {
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const progress = totalDuration > 0 ? Math.min(1, (totalDuration - timeRemaining) / totalDuration) : 0;

  return {
    timeRemaining,
    totalDuration,
    isRunning,
    isPaused,
    progress,
    start,
    pause,
    resume,
    togglePause,
    stop,
  };
}
